import {
  MARKER_STYLES,
  MAX_MARKER_LABELS,
} from '@modules/consolidator/constants/AssetMarkers'
import {
  APLICACAO_PATTERN,
  RESGATE_PATTERN,
} from '@modules/consolidator/constants/AssetMovements'

const MAX_TOOLTIP_ITEMS = 4

/**
 * Converte 'DD/MM/YYYY' ou 'YYYY-MM-DD' em inteiro YYYYMMDD comparável.
 * Parse por componentes de string — sem Date/moment de propósito: datas civis
 * do backend não podem sofrer deslocamento de timezone (aawzDate formata em
 * UTC e desloca o dia perto da meia-noite GMT-3).
 */
const splitDateParts = (trimmed) => {
  if (trimmed.includes('/')) {
    const parts = trimmed.split('/')
    return parts.length === 3
      ? { day: parts[0], month: parts[1], year: parts[2] }
      : null
  }
  if (trimmed.includes('-')) {
    const parts = trimmed.split('-')
    return parts.length === 3
      ? { day: parts[2], month: parts[1], year: parts[0] }
      : null
  }
  return null
}

export const dateToComparableKey = (value) => {
  if (!value || typeof value !== 'string') return null
  const parts = splitDateParts(value.trim().slice(0, 10))
  if (!parts) return null
  const key =
    Number(parts.year) * 10000 + Number(parts.month) * 100 + Number(parts.day)
  return Number.isFinite(key) && key > 0 ? key : null
}

/** Primeiro índice de labelKeys (ordenado) com valor >= key; -1 se não há. */
const firstLabelIndexAtOrAfter = (labelKeys, key) => {
  let low = 0
  let high = labelKeys.length - 1
  if (!labelKeys.length || key > labelKeys[high]) return -1
  while (low < high) {
    const mid = (low + high) >> 1
    if (labelKeys[mid] < key) low = mid + 1
    else high = mid
  }
  return low
}

const categorizeMovement = (movement) => {
  const operacao = movement.operacao || ''
  if (RESGATE_PATTERN.test(operacao)) {
    return 'resgate'
  }
  if (APLICACAO_PATTERN.test(operacao)) {
    return 'aplicacao'
  }
  return null
}

const collectMarkerItems = ({ movements, proventos, splits }) => {
  const items = []
  ;(movements || []).forEach((movement) => {
    const category = categorizeMovement(movement)
    if (!category) {
      return
    }
    items.push({
      category,
      date: movement.data_cotizacao || movement.data_aplicacao,
      source: movement,
    })
  })
  ;(proventos || []).forEach((provento) => {
    items.push({
      category: 'evento',
      date: provento.data_pagamento || provento.data_com,
      source: provento,
    })
  })
  ;(splits || []).forEach((split) => {
    items.push({
      category: 'split',
      date: split.data_com || split.data_pagamento,
      source: split,
    })
  })
  return items
}

const monthOfKey = (key) => Math.floor(key / 100)

/** Índice do primeiro label do mês do label recebido. */
const firstLabelIndexOfMonth = (labelKeys, labelIndex) => {
  const month = monthOfKey(labelKeys[labelIndex])
  let index = labelIndex
  while (index > 0 && monthOfKey(labelKeys[index - 1]) === month) {
    index -= 1
  }
  return index
}

/**
 * Constrói os goals[] do chartjs-goal-markers a partir das movimentações,
 * proventos e splits do ativo, casando cada item com o primeiro label
 * (data 'DD/MM/YYYY' do eixo X) igual ou posterior à data do item.
 *
 * - Item POSTERIOR ao último label é sempre descartado.
 * - Item ANTERIOR ao primeiro label só ancora no primeiro quando `ancorarAnteriores`
 *   é true (a janela mais larga, onde ele é o aporte de abertura). Nas janelas
 *   estreitas ele é descartado, senão o marcador afirmaria uma data que não é a dele.
 * - Itens do mesmo label e mesma categoria viram UM goal com raw.items[]
 *   (o plugin já empilha goals de categorias diferentes no mesmo label).
 * - Acima de MAX_MARKER_LABELS datas distintas, o snap vira mensal.
 */
export const buildAssetGoalMarkers = ({
  movements,
  proventos,
  splits,
  labels,
  ancorarAnteriores = false,
}) => {
  if (!Array.isArray(labels) || !labels.length) return []

  const labelKeys = labels.map(dateToComparableKey)
  const items = collectMarkerItems({ movements, proventos, splits })

  const snapped = []
  items.forEach((item) => {
    const key = dateToComparableKey(item.date)
    if (key === null) return
    const labelIndex = firstLabelIndexAtOrAfter(labelKeys, key)
    if (labelIndex === -1) return
    if (key < labelKeys[0] && !ancorarAnteriores) return
    snapped.push({ ...item, labelIndex })
  })
  if (!snapped.length) return []

  const distinctLabels = new Set(snapped.map((item) => item.labelIndex))
  if (distinctLabels.size > MAX_MARKER_LABELS) {
    snapped.forEach((item) => {
      item.labelIndex = firstLabelIndexOfMonth(labelKeys, item.labelIndex)
    })
  }

  const goalsByKey = new Map()
  snapped.forEach((item) => {
    const groupKey = `${item.labelIndex}:${item.category}`
    if (!goalsByKey.has(groupKey)) {
      const style = MARKER_STYLES[item.category]
      goalsByKey.set(groupKey, {
        index: labels[item.labelIndex],
        name: style.name,
        color: style.color,
        symbol: style.symbol,
        icon: null,
        raw: {
          category: item.category,
          targetTab: style.targetTab,
          date: labels[item.labelIndex],
          items: [],
        },
      })
    }
    goalsByKey.get(groupKey).raw.items.push(item.source)
  })

  return Array.from(goalsByKey.values()).sort(
    (a, b) => dateToComparableKey(a.index) - dateToComparableKey(b.index)
  )
}

/**
 * Conteúdo do tooltip custom de um marcador ({ title, lines }), no contrato
 * de goalMarkerOptions.tooltip.getContent do chartjs-goal-markers.
 * @param {Function} formatItem Formata um item do goal em uma linha de texto.
 */
export const buildMarkerTooltipContent = (goal, formatItem) => {
  const raw = goal.raw || {}
  const items = Array.isArray(raw.items) ? raw.items : []
  const lines = items
    .slice(0, MAX_TOOLTIP_ITEMS)
    .map((item) => formatItem(item, raw.category))
    .filter(Boolean)
  if (items.length > MAX_TOOLTIP_ITEMS) {
    lines.push(`+${items.length - MAX_TOOLTIP_ITEMS} outras`)
  }
  lines.push('Clique para ver detalhes')
  return {
    title: `${goal.name} · ${raw.date || goal.index}`,
    lines: lines,
  }
}
